import { useState, useEffect, type FormEvent } from 'react';
import { X, Trash2, Save, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import type { AgentData } from '../lib/api';
import { useSaveAgent, useDeleteAgent } from '../hooks/useAgents';
import { useModels, useTools } from '../hooks/useModels';

interface Props {
  agent: AgentData | null; // null = create new
  onClose: () => void;
}

const EXECUTION_MODES = ['standard', 'workflow', 'crew', 'pipeline'] as const;
const THINKING_LEVELS = ['off', 'minimal', 'low', 'medium', 'high', 'xhigh'] as const;
const DM_SCOPES = ['', 'exclusive', 'included', 'excluded'] as const;
const FAILURE_POLICIES = ['', 'fail-fast', 'continue', 'retry'] as const;

export function AgentEditor({ agent, onClose }: Props) {
  const { data: modelData } = useModels();
  const { data: availableTools } = useTools();
  const saveAgent = useSaveAgent();
  const deleteAgent = useDeleteAgent();

  const isNew = !agent;

  // ── Core fields ──
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState('');
  const [model, setModel] = useState('');
  const [thinking, setThinking] = useState('');
  const [mode, setMode] = useState('standard');
  const [prompt, setPrompt] = useState('');

  // ── Tools & skills ──
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [customTools, setCustomTools] = useState('');
  const [skills, setSkills] = useState('');

  // ── Routing ──
  const [dmScope, setDmScope] = useState('');
  const [defaultForChannels, setDefaultForChannels] = useState('');
  const [isFactoryAgent, setIsFactoryAgent] = useState(false);

  // ── Isolation & memory ──
  const [runtimeId, setRuntimeId] = useState('');
  const [sharedScopes, setSharedScopes] = useState('');

  // ── Mesh ──
  const [mesh, setMesh] = useState(false);
  const [meshAutoJoin, setMeshAutoJoin] = useState(false);

  // ── Pipeline ──
  const [pipelinePreset, setPipelinePreset] = useState('');
  const [pipelineMaxRetry, setPipelineMaxRetry] = useState('');

  // ── Crew ──
  const [crewPlanner, setCrewPlanner] = useState('');
  const [crewWorker, setCrewWorker] = useState('');
  const [crewReviewer, setCrewReviewer] = useState('');
  const [crewConcurrency, setCrewConcurrency] = useState('');
  const [crewFailure, setCrewFailure] = useState('');

  // ── Extra ──
  const [agentType, setAgentType] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [defaultReads, setDefaultReads] = useState('');
  const [output, setOutput] = useState('');

  // ── Sections ──
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['core']));

  function toggleSection(s: string) {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }

  useEffect(() => {
    if (agent) {
      setName(agent.name);
      setDescription(agent.description ?? '');
      setTrigger(agent.trigger ?? `@${agent.name}`);
      setModel(agent.model ?? '');
      setThinking(agent.thinking ?? '');
      setMode(agent.executionMode ?? 'standard');
      setSelectedTools(agent.tools ?? []);
      setSkills((agent.skills ?? []).join(', '));
      setDmScope(agent.dmScope ?? '');
      setDefaultForChannels((agent.defaultForChannels ?? []).join(', '));
      setIsFactoryAgent(agent.isFactoryAgent ?? false);
      setRuntimeId(agent.runtimeId ?? '');
      setSharedScopes((agent.sharedScopes ?? []).join(', '));
      setMesh(agent.mesh ?? false);
      setMeshAutoJoin(agent.meshAutoJoin ?? false);
      setPipelinePreset(agent.pipelinePreset ?? '');
      setPipelineMaxRetry(agent.pipelineMaxRetryLoops?.toString() ?? '');
      setCrewPlanner(agent.crewPlannerAgent ?? '');
      setCrewWorker(agent.crewWorkerAgent ?? '');
      setCrewReviewer(agent.crewReviewerAgent ?? '');
      setCrewConcurrency(agent.crewConcurrency?.toString() ?? '');
      setCrewFailure(agent.crewFailurePolicy ?? '');
      setAgentType(agent.type ?? '');
      setCapabilities((agent.capabilities ?? []).join(', '));
      setDefaultReads((agent.defaultReads ?? []).join(', '));
      setOutput(agent.output ?? '');
      // Prompt from source
      if (agent.source) {
        const parts = agent.source.split('---');
        setPrompt((parts[2] ?? '').trim());
      } else {
        setPrompt(agent.systemPrompt ?? '');
      }
      // Open relevant sections
      const sections = new Set(['core']);
      if (agent.mesh) sections.add('mesh');
      if (agent.executionMode === 'pipeline') sections.add('pipeline');
      if (agent.executionMode === 'crew') sections.add('crew');
      if (agent.runtimeId || agent.sharedScopes?.length) sections.add('isolation');
      setOpenSections(sections);
    }
  }, [agent]);

  function splitCsv(s: string): string[] {
    return s.split(',').map(x => x.trim()).filter(Boolean);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;

    const allTools = [...selectedTools, ...splitCsv(customTools)];
    const uniqueTools = [...new Set(allTools)];

    const body: Record<string, unknown> = {
      description,
      trigger: trigger || `@${n}`,
      model: model || undefined,
      thinking: thinking || undefined,
      tools: uniqueTools,
      skills: splitCsv(skills).length ? splitCsv(skills) : undefined,
      executionMode: mode,
      systemPrompt: prompt,
      type: agentType || undefined,
      capabilities: splitCsv(capabilities).length ? splitCsv(capabilities) : undefined,
      defaultReads: splitCsv(defaultReads).length ? splitCsv(defaultReads) : undefined,
      output: output || undefined,
      dmScope: dmScope || undefined,
      defaultForChannels: splitCsv(defaultForChannels).length ? splitCsv(defaultForChannels) : undefined,
      isFactoryAgent: isFactoryAgent || undefined,
      runtimeId: runtimeId || undefined,
      sharedScopes: splitCsv(sharedScopes).length ? splitCsv(sharedScopes) : undefined,
      mesh: mesh || undefined,
      meshAutoJoin: meshAutoJoin || undefined,
      pipelinePreset: pipelinePreset || undefined,
      pipelineMaxRetryLoops: pipelineMaxRetry ? parseInt(pipelineMaxRetry) : undefined,
      crewPlannerAgent: crewPlanner || undefined,
      crewWorkerAgent: crewWorker || undefined,
      crewReviewerAgent: crewReviewer || undefined,
      crewConcurrency: crewConcurrency ? parseInt(crewConcurrency) : undefined,
      crewFailurePolicy: crewFailure || undefined,
    };

    await saveAgent.mutateAsync({ name: n, body });
    onClose();
  }

  async function handleDelete() {
    if (!agent || !confirm(`Delete agent "${agent.name}"?`)) return;
    await deleteAgent.mutateAsync(agent.name);
    onClose();
  }

  const saving = saveAgent.isPending;
  const deleting = deleteAgent.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-surface border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-surface z-10">
          <h2 className="font-semibold text-sm">{isNew ? 'New Agent' : `Edit: ${agent.name}`}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-1">
          {/* ── Core ── */}
          <Section title="Core" id="core" open={openSections} toggle={toggleSection}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <input value={name} onChange={e => setName(e.target.value)} disabled={!isNew} placeholder="my-agent" className="input" required />
              </Field>
              <Field label="Trigger">
                <input value={trigger} onChange={e => setTrigger(e.target.value)} placeholder="@my-agent" className="input" />
              </Field>
            </div>
            <Field label="Description">
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What this agent does" className="input" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Model">
                <select value={model} onChange={e => setModel(e.target.value)} className="input text-xs">
                  <option value="">Factory default</option>
                  {modelData?.models.map(m => (
                    <option key={m.label} value={m.label}>{m.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Thinking">
                <select value={thinking} onChange={e => setThinking(e.target.value)} className="input">
                  <option value="">Default</option>
                  {THINKING_LEVELS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Execution Mode">
                <select value={mode} onChange={e => setMode(e.target.value)} className="input">
                  {EXECUTION_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          {/* ── Tools ── */}
          <Section title="Tools & Skills" id="tools" open={openSections} toggle={toggleSection}>
            <Field label="Tools">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(availableTools ?? []).map(t => {
                  const active = selectedTools.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSelectedTools(prev =>
                        active ? prev.filter(x => x !== t) : [...prev, t]
                      )}
                      className={`text-[10px] px-2 py-1 rounded font-mono border transition-colors ${
                        active
                          ? 'bg-accent/20 border-accent/50 text-accent-2'
                          : 'bg-surface-2 border-border text-zinc-500 hover:border-zinc-500'
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <input value={customTools} onChange={e => setCustomTools(e.target.value)} placeholder="Additional custom tools (comma-separated)" className="input font-mono text-xs" />
            </Field>
            <Field label="Skills (comma-separated)">
              <input value={skills} onChange={e => setSkills(e.target.value)} placeholder="e.g. code-review, web-search" className="input text-xs" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Default Reads (files to auto-read)">
                <input value={defaultReads} onChange={e => setDefaultReads(e.target.value)} placeholder="README.md, docs/arch.md" className="input text-xs font-mono" />
              </Field>
              <Field label="Output file">
                <input value={output} onChange={e => setOutput(e.target.value)} placeholder="output.md" className="input text-xs font-mono" />
              </Field>
            </div>
          </Section>

          {/* ── Routing ── */}
          <Section title="Routing" id="routing" open={openSections} toggle={toggleSection}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="DM Scope">
                <select value={dmScope} onChange={e => setDmScope(e.target.value)} className="input">
                  {DM_SCOPES.map(d => <option key={d} value={d}>{d || 'Default'}</option>)}
                </select>
              </Field>
              <Field label="Default for Channels">
                <input value={defaultForChannels} onChange={e => setDefaultForChannels(e.target.value)} placeholder="general, random" className="input text-xs" />
              </Field>
            </div>
            <Checkbox label="Is Factory Agent (meta-agent)" checked={isFactoryAgent} onChange={setIsFactoryAgent} />
          </Section>

          {/* ── Isolation & Memory ── */}
          <Section title="Isolation & Memory" id="isolation" open={openSections} toggle={toggleSection}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Runtime ID (workspace isolation)">
                <input value={runtimeId} onChange={e => setRuntimeId(e.target.value)} placeholder="default" className="input text-xs font-mono" />
              </Field>
              <Field label="Shared Memory Scopes">
                <input value={sharedScopes} onChange={e => setSharedScopes(e.target.value)} placeholder="team, project-x" className="input text-xs" />
              </Field>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1">
              Agents with different runtimeIds get separate workspaces. Shared scopes let agents read/write to common memory pools.
            </p>
          </Section>

          {/* ── Mesh ── */}
          <Section title="Agent Mesh" id="mesh" open={openSections} toggle={toggleSection}>
            <div className="flex gap-6">
              <Checkbox label="Enable mesh (messaging, reservations)" checked={mesh} onChange={setMesh} />
              <Checkbox label="Auto-join on spawn" checked={meshAutoJoin} onChange={setMeshAutoJoin} />
            </div>
          </Section>

          {/* ── Pipeline (show only when mode=pipeline) ── */}
          {mode === 'pipeline' && (
            <Section title="Pipeline Config" id="pipeline" open={openSections} toggle={toggleSection}>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Preset">
                  <input value={pipelinePreset} onChange={e => setPipelinePreset(e.target.value)} placeholder="scout-plan-work-review" className="input text-xs" />
                </Field>
                <Field label="Max Retry Loops">
                  <input type="number" value={pipelineMaxRetry} onChange={e => setPipelineMaxRetry(e.target.value)} placeholder="2" className="input text-xs" min="0" max="10" />
                </Field>
              </div>
            </Section>
          )}

          {/* ── Crew (show only when mode=crew) ── */}
          {mode === 'crew' && (
            <Section title="Crew Config" id="crew" open={openSections} toggle={toggleSection}>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Planner Agent">
                  <input value={crewPlanner} onChange={e => setCrewPlanner(e.target.value)} placeholder="planner" className="input text-xs" />
                </Field>
                <Field label="Worker Agent">
                  <input value={crewWorker} onChange={e => setCrewWorker(e.target.value)} placeholder="worker" className="input text-xs" />
                </Field>
                <Field label="Reviewer Agent">
                  <input value={crewReviewer} onChange={e => setCrewReviewer(e.target.value)} placeholder="reviewer" className="input text-xs" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Concurrency">
                  <input type="number" value={crewConcurrency} onChange={e => setCrewConcurrency(e.target.value)} placeholder="3" className="input text-xs" min="1" max="20" />
                </Field>
                <Field label="Failure Policy">
                  <select value={crewFailure} onChange={e => setCrewFailure(e.target.value)} className="input">
                    {FAILURE_POLICIES.map(f => <option key={f} value={f}>{f || 'Default'}</option>)}
                  </select>
                </Field>
              </div>
            </Section>
          )}

          {/* ── Advanced ── */}
          <Section title="Advanced" id="advanced" open={openSections} toggle={toggleSection}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Agent Type">
                <input value={agentType} onChange={e => setAgentType(e.target.value)} placeholder="e.g. scout, worker" className="input text-xs" />
              </Field>
              <Field label="Capabilities (comma-separated)">
                <input value={capabilities} onChange={e => setCapabilities(e.target.value)} placeholder="code, research, review" className="input text-xs" />
              </Field>
            </div>
          </Section>

          {/* ── System Prompt ── */}
          <Section title="System Prompt" id="prompt" open={openSections} toggle={toggleSection}>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="You are a helpful agent that..."
              rows={12}
              className="input font-mono text-xs resize-y min-h-[160px]"
            />
          </Section>

          {/* ── Actions ── */}
          <div className="flex items-center gap-2 pt-4 border-t border-border mt-4">
            {!isNew && (
              <button type="button" onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <div className="flex-1" />
            <button type="button" onClick={onClose} className="px-3 py-2 text-xs text-zinc-400 hover:text-zinc-200">Cancel</button>
            <button type="submit" disabled={saving || !name.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-accent hover:bg-accent-2 text-white rounded-lg transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function Section({ title, id, open, toggle, children }: {
  title: string; id: string; open: Set<string>;
  toggle: (s: string) => void; children: React.ReactNode;
}) {
  const isOpen = open.has(id);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button type="button" onClick={() => toggle(id)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-zinc-300 bg-surface-2 hover:bg-surface-3 transition-colors">
        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {title}
      </button>
      {isOpen && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-zinc-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        className="rounded border-border bg-zinc-900 text-accent focus:ring-accent" />
      {label}
    </label>
  );
}
